/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';

import { selectDraftEnvironmentId } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../ts';
import {
  CreateTestResponse,
  DeleteTestResponse,
  TestResultsResponse,
  TestSettingsResponse
} from '../../../../ts/insight-actions';
import type { CreateAsyncThunk } from '../../../../ts/state';
import * as api from '../../api/insight-actions/index';
import { selectSkillLanguage } from '../skills/core-selectors';

export const createTest = createAsyncThunk<
  CreateTestResponse,
  Array<{ input: string }>,
  CreateAsyncThunk
>(
  'insightActions/createTest',
  async (data, { dispatch, getState }) => {
    const assistantId = getAssistantId(getState());
    const environmentId = selectDraftEnvironmentId(getState());

    try {
      const response = await api.postTestData(assistantId, environmentId, data);

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { status } = getState().insightActions.createTest;
      if (status === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const deleteTest = createAsyncThunk<
  DeleteTestResponse,
  string,
  CreateAsyncThunk
>(
  'insightActions/deleteTest',
  async (testId, { dispatch, getState }) => {
    const assistantId = getAssistantId(getState());
    const environmentId = selectDraftEnvironmentId(getState());

    try {
      const response = await api.deleteTest(assistantId, environmentId, testId);
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { status } = getState().insightActions.deleteTest;
      if (status === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

// Provides the scores and results from the testing evaluation
export const getTestResults = createAsyncThunk<
  TestResultsResponse,
  { testId: string; recognitionTypes: string[] },
  CreateAsyncThunk
>(
  'insightActions/getTestResults',
  async ({ testId, recognitionTypes }, { dispatch, getState }) => {
    // For analytics store the env ID is the assistant ID
    const assistantId = selectDraftEnvironmentId(getState());
    const language = selectSkillLanguage(getState()) || 'en';
    try {
      const response = await api.getTestResults(
        language,
        assistantId,
        testId,
        recognitionTypes
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { status } = getState().insightActions.testResults;
      if (status === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const exportTestResults = createAsyncThunk<
  void,
  string,
  CreateAsyncThunk
>(
  'insightActions/exportTestResults',
  async (testId, { dispatch, getState }) => {
    // For analytics store the env ID is the assistant ID
    const assistantId = selectDraftEnvironmentId(getState());
    const language = selectSkillLanguage(getState()) || 'en';
    try {
      await api.exportTestResults(language, assistantId, testId);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { status } = getState().insightActions.exportResults;
      if (status === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const getTestSettings = createAsyncThunk<
  TestSettingsResponse,
  string,
  CreateAsyncThunk
>(
  'insightActions/getTestSettings',
  async (testId, { dispatch, getState }) => {
    const assistantId = getAssistantId(getState());
    const environmentId = selectDraftEnvironmentId(getState());

    try {
      const response = await api.getTestSettings(
        assistantId,
        environmentId,
        testId
      );
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { status } = getState().insightActions.testSettings;
      if (status === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
